import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'

import type { UploadPhase } from '@/components/FileUpload'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CreateStepParams } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { castStepToZodForm } from '@/utils/contentModuleForm'
import {
  checkYouTubeEmbeddable,
  getUrlLikenessError,
  getYouTubeUrlError,
  getYouTubeVideoId,
} from '@/utils/urlValidation'

import type { CMFormSection } from './ContentModuleFormScreen'
import { useStepResources } from './useStepResources'

export function useUploadModalState(props: CMFormSection) {
  const { uiStore, api } = useGlobalState()

  const [isExtracting, setIsExtracting] = useState(false)
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [websiteUrlError, setWebsiteUrlError] = useState<string | null>(null)
  const [embedWarning, setEmbedWarning] = useState<string | null>(null)
  const [pendingFileName, setPendingFileName] = useState<string | null>(null)

  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [processProgressPct, setProcessProgressPct] = useState(0)

  const [stepCreationAbortController, setStepCreationAbortController] = useState<AbortController | null>(null)
  const [createdStepId, setCreatedStepId] = useState<string | null>(null)

  const { stepResources, uploadedFileResource, sourceFileResource } = useStepResources(createdStepId)

  const {
    uploadProgress,
    upload: uploadContent,
    cancel: cancelContentUpload,
  } = useFileUpload({
    onPrepareError: () => {
      props.stepForm.setFieldError('content.file', 'Something went wrong. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadError: () => {
      props.stepForm.setFieldError('content.file', 'Upload failed. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadSuccess: () => props.stepForm.setFieldError('content.file', undefined),
    onUploadCanceled: () => {
      setUploadPhase('idle')
      setProcessProgressPct(0)
    },
  })

  async function createStepWithAbortSupport(params: CreateStepParams) {
    const abortController = new AbortController()
    setStepCreationAbortController(abortController)

    try {
      const response = await api.createStep(params, abortController.signal, { waitForProcessing: false })

      setCreatedStepId(response.stepId)

      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      const steps = await props.stepsQuery.refetch()
      const step = steps.data?.find(step => step.stepId === response.stepId)

      // `refetch` takes React Query's default `cancelRefetch: true`, and a settle landing
      // mid-request cancels it silently — the promise resolves with the pre-create list
      // rather than rejecting, so a miss here does not mean the step isn't there. The
      // create response carries what a brand-new step has, so seed from it instead of
      // leaving the form without a `stepId` and the modal gated on a file that exists.
      props.stepForm.setValues(
        castStepToZodForm(
          step ?? {
            stepId: response.stepId,
            name: response.generated?.name,
            introduction: response.generated?.introduction,
          },
        ),
      )

      return response
    } catch (error) {
      if (abortController.signal.aborted) {
        console.log('[UploadStepModal] File creation was cancelled')
        return null
      }

      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      throw error
    } finally {
      setStepCreationAbortController(null)
    }
  }

  async function handleExtractFromWebsite() {
    if (!websiteUrl.trim()) return

    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
      return
    }

    const youtubeError = getYouTubeUrlError(websiteUrl.trim())
    if (youtubeError) {
      setWebsiteUrlError(youtubeError)
      return
    }
    setWebsiteUrlError(null)
    setEmbedWarning(null)

    setIsExtracting(true)
    setUploadPhase('processing')
    setProcessProgressPct(50)

    try {
      // Non-blocking: warn (but still allow the add) if a YouTube video can't be embedded.
      if (getYouTubeVideoId(websiteUrl.trim())) {
        const embed = await checkYouTubeEmbeddable(websiteUrl.trim())
        if (embed.status === 'not-embeddable') {
          setEmbedWarning(
            embed.reason === 'not-found'
              ? "This video couldn't be found on YouTube. It may have been deleted or the link may be wrong."
              : 'This video may not play for members. It could be private or have embedding turned off by the owner. You can still add it.',
          )
        }
      }

      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
      await createStepWithAbortSupport({
        task_id: props.taskId!,
        url: websiteUrl,
        file_name: websiteUrl,
        use_llm_for_scraping: false,
        preceding_step_id: lastStep?.stepId,
      })
      uiStore.setPendingStepDeletion(true)
    } catch (error) {
      console.error('Error extracting from website:', error)
      notifications.show({
        title: 'Extraction Failed',
        message: 'Could not start content extraction. Please check the URL or try again later.',
        color: 'red',
      })
    } finally {
      setIsExtracting(false)
      setUploadPhase('idle')
      setProcessProgressPct(0)
    }
  }

  async function processDroppedFiles(files: FileWithPath[]) {
    const file = files[0]

    setUploadPhase('uploading')
    const uploadResponse = await uploadContent(file)

    if (!uploadResponse) {
      setUploadPhase('idle')
      return
    }

    setPendingFileName(null)
    setUploadPhase('idle')

    try {
      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
      await createStepWithAbortSupport({
        task_id: props.taskId!,
        file_name: file.name,
        s3_file: uploadResponse.filePath,
        preceding_step_id: lastStep?.stepId,
      })
      uiStore.setPendingStepDeletion(true)
    } catch {
      // createStepWithAbortSupport already shows a notification on failure
    }
  }

  function cancelStepCreation() {
    if (stepCreationAbortController) {
      console.log('[UploadStepModal] Cancelling step creation...')
      stepCreationAbortController.abort()
      setStepCreationAbortController(null)
    }
  }

  const isStepCreating = !!stepCreationAbortController
  const hasContent = !!props.stepContentQuery.data
  const contentLoading = uploadPhase !== 'idle' || isStepCreating || props.stepContentQuery.isFetching

  function updateWebsiteUrl(value: string | ((prev: string) => string)) {
    setWebsiteUrl(value)
    if (websiteUrlError) setWebsiteUrlError(null)
    if (embedWarning) setEmbedWarning(null)
  }

  function handleWebsiteUrlBlur() {
    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
    }
  }

  return {
    stepResources,

    isExtracting,
    websiteUrl,
    setWebsiteUrl: updateWebsiteUrl,
    // Raw setter for programmatic pre-fill; unlike updateWebsiteUrl it doesn't clear the embed warning.
    setWebsiteUrlRaw: setWebsiteUrl,
    websiteUrlError,
    embedWarning,
    handleWebsiteUrlBlur,
    handleExtractFromWebsite,

    pendingFileName,
    setPendingFileName,

    uploadPhase,
    uploadProgress,
    processProgressPct,

    cancelContentUpload,
    processDroppedFiles,

    isStepCreating,
    cancelStepCreation,

    hasContent,
    contentLoading,
    uploadedFileResource,
    sourceFileResource,
  }
}
